import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

export type PlanType = 'rate_based' | 'lifetime_cap';

export interface LifetimeCapLimits {
  max_active_videos?: number;
}

export interface PricingTier {
  name: string;
  level: number;
  product_id: string;
  plan_type?: PlanType;
  prices: {
    month: {
      value: number | string;
      stripePriceId: string;
    };
    year: {
      value: number | string;
      stripePriceId: string;
    };
  };
  subtext: string;
  monthlyVideos: number;
  videoDuration: string;
  videoQuality: string;
  video_seconds: number;
  videoQualityNumber: number;
  storageDuration: string;
  storageType?: 'unlimited' | '30day';
  features: {
    aiTitle: boolean;
    aiSummary: boolean;
    aiChapters: boolean;
    aiContentAnalysis: boolean;
    aiKeyPoints: boolean;
    aiTagging: boolean;
    aiTagManagement: boolean;
    audioRecording: boolean;
    commenting: boolean;
    teamCollaboration: boolean;
    analytics: boolean;
    adminControls: boolean;
    desktopDownload: boolean;
  };
  betaFeatures?: Record<string, boolean>;
  limits: string[] | LifetimeCapLimits;
  /**
   * Lifetime cap on AI-processed notes. null/undefined = unlimited.
   * Top-level on each plan object — see CONTRACT.md #8.
   */
  max_ai_notes?: number | null;
  featureList: string[];
  active?: boolean;
  lastUpdated?: string;
  /**
   * Default seat allotment for team plans. Required when
   * features.teamCollaboration === true. Read live by the API at invite
   * time — never copied onto the org doc.
   */
  default_seats?: number;
}

export interface PricingData {
  [key: string]: PricingTier;
}

const PRICING_COLLECTION = 'pricing';
const PRICING_DOC_ID = 'current';

/**
 * Fetch pricing data from Firebase
 */
export const fetchPricingData = async (): Promise<PricingData | null> => {
  try {
    const docRef = doc(db, PRICING_COLLECTION, PRICING_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as PricingData;
    } else {
      return null;
    }
  } catch (error) {
    if (isFirebaseOfflineError(error)) {
      errorReporter.expected('Failed to fetch pricing data (client offline)', error, {
        feature: 'firebase',
        operation: 'fetchPricingData',
      });
    } else {
      // Critical: Pricing data is essential for plan management
      errorReporter.critical('Failed to fetch pricing data from Firebase', error, {
        feature: 'subscription',
        operation: 'fetchPricingData',
      });
    }
    return null;
  }
};

/**
 * Fetch all pricing configurations (for admin purposes)
 */
export const fetchAllPricingConfigs = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, PRICING_COLLECTION));
    const configs: Array<{ id: string; data: PricingData }> = [];

    querySnapshot.forEach((doc) => {
      configs.push({
        id: doc.id,
        data: doc.data() as PricingData,
      });
    });

    return configs;
  } catch (error) {
    if (isFirebaseOfflineError(error)) {
      errorReporter.expected('Failed to fetch pricing configurations (client offline)', error, {
        feature: 'firebase',
        operation: 'fetchAllPricingConfigs',
      });
    } else {
      // Critical: Admin pricing configs failure should be tracked
      errorReporter.critical('Failed to fetch pricing configurations', error, {
        feature: 'subscription',
        operation: 'fetchAllPricingConfigs',
      });
    }
    return [];
  }
};
